import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { useCartStore } from '../stores/cartStore';
import { useAuthStore } from '../stores/authStore';
import { usePrice } from '../hooks/usePrice';

const WEB_URL = 'https://www.outlawoasis.com';
const OG_DEFAULT = `${WEB_URL}/og-image.png`;

export default function ProductDetail() {
  const { formatPrice } = usePrice();
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated } = useAuthStore();
  const { addItem } = useCartStore();

  const { data, isLoading } = useQuery({
    queryKey: ['product', id],
    queryFn: () => api.get(`/products/${id}`),
    enabled: !!id,
  });

  const product = (data as any)?.data?.product;

  // JSON-LD Product structured data
  useEffect(() => {
    if (!product || !id) return;
    const images = [product.thumbnailUrl, ...(product.images || [])].filter(Boolean);
    const jsonLd: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: product.shortDescription || product.description,
      url: `${WEB_URL}/products/${id}`,
      ...(images.length && { image: images }),
      offers: {
        '@type': 'Offer',
        priceCurrency: 'USD',
        price: product.price,
        availability:
          product.availableStock === 0
            ? 'https://schema.org/OutOfStock'
            : 'https://schema.org/InStock',
        seller: { '@type': 'Organization', name: product.seller.storeName },
      },
      ...(product.ratingCount > 0 && {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: parseFloat((product.ratingAverage || 0).toFixed(1)),
          reviewCount: product.ratingCount,
        },
      }),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'seo-product-jsonld';
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);
    return () => document.getElementById('seo-product-jsonld')?.remove();
  }, [product, id]);

  const handleAddToCart = async () => {
    try {
      await addItem(id!, 1, isAuthenticated);
      toast.success('Added to cart!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to add to cart');
    }
  };

  if (isLoading) {
    return (
      <div className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-leather rounded w-1/3 mb-8" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="aspect-video bg-leather rounded-lg" />
              <div className="space-y-4">
                <div className="h-6 bg-leather rounded w-1/4" />
                <div className="h-10 bg-leather rounded w-3/4" />
                <div className="h-4 bg-leather rounded w-1/3" />
                <div className="h-20 bg-leather rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="py-16 px-4 text-center">
        <h1 className="font-western text-3xl text-cream mb-4">Product Not Found</h1>
        <Link to="/products" className="text-gold hover:text-gold-bright">
          &larr; Back to Products
        </Link>
      </div>
    );
  }

  const metaTitle = `${product.name} – Buy on Outlaw Oasis`;
  const metaDesc =
    product.shortDescription ||
    `${product.description.slice(0, 155)}…`;
  const ogImage = product.thumbnailUrl || OG_DEFAULT;
  const canonicalUrl = `${WEB_URL}/products/${id}`;

  return (
    <div className="py-8 px-4">
      {/* SEO */}
      <title>{metaTitle}</title>
      <meta name="description" content={metaDesc} />
      <link rel="canonical" href={canonicalUrl} />
      {product.tags?.length > 0 && (
        <meta name="keywords" content={product.tags.map((t: any) => t.name).join(', ')} />
      )}
      {/* Open Graph */}
      <meta property="og:type" content="product" />
      <meta property="og:site_name" content="Outlaw Oasis" />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={product.name} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="product:price:amount" content={String(product.price)} />
      <meta property="product:price:currency" content="USD" />
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@outlawoasis" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDesc} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={product.name} />

      <div className="max-w-6xl mx-auto">
        {/* Breadcrumb */}
        <nav className="mb-8 text-sm">
          <ol className="flex items-center gap-2 text-text-secondary">
            <li><Link to="/" className="hover:text-cream">Home</Link></li>
            <li>/</li>
            <li><Link to="/products" className="hover:text-cream">Products</Link></li>
            <li>/</li>
            <li><Link to={`/products?category=${product.category.id}`} className="hover:text-cream">{product.category.name}</Link></li>
            <li>/</li>
            <li className="text-cream">{product.name}</li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images */}
          <div>
            <div className="aspect-video bg-leather rounded-lg overflow-hidden mb-4">
              {product.thumbnailUrl ? (
                <img
                  src={product.thumbnailUrl}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-gold/30 font-western text-4xl">OO</span>
                </div>
              )}
            </div>
            {product.images && product.images.length > 0 && (
              <div className="grid grid-cols-4 gap-2">
                {product.images.map((img: string, i: number) => (
                  <div key={i} className="aspect-video bg-leather rounded overflow-hidden">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            {/* Seller */}
            <Link
              to={`/sellers/${product.seller.storeSlug}`}
              className="inline-flex items-center gap-2 mb-4 text-text-secondary hover:text-cream transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-leather border border-gold/30 flex items-center justify-center overflow-hidden">
                {product.seller.storeLogoUrl ? (
                  <img src={product.seller.storeLogoUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-gold text-sm">{product.seller.storeName[0]}</span>
                )}
              </div>
              <span>{product.seller.storeName}</span>
            </Link>

            {/* Title */}
            <h1 className="font-western text-3xl text-cream mb-4">{product.name}</h1>

            {/* Rating */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    className={star <= Math.round(product.ratingAverage || 0) ? 'text-gold' : 'text-text-muted'}
                  >
                    &#9733;
                  </span>
                ))}
                <span className="ml-2 text-text-secondary">
                  {product.ratingAverage?.toFixed(1) || '0.0'} ({product.ratingCount} reviews)
                </span>
              </div>
              <span className="text-text-muted">|</span>
              <span className="text-text-secondary">{product.salesCount} sold</span>
            </div>

            {/* Price */}
            <div className="mb-6">
              <div className="flex items-center gap-3">
                <span className="text-3xl font-bold text-gold">{formatPrice(product.price)}</span>
                {product.compareAtPrice && (
                  <>
                    <span className="text-xl text-text-muted line-through">
                      {formatPrice(product.compareAtPrice)}
                    </span>
                    <span className="px-2 py-1 bg-hot/20 text-hot text-sm rounded">
                      {Math.round((1 - product.price / product.compareAtPrice) * 100)}% OFF
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Description */}
            {product.shortDescription && (
              <p className="text-text-secondary mb-6">{product.shortDescription}</p>
            )}

            {/* Stock */}
            <div className="mb-6">
              {product.availableStock === null ? (
                <span className="text-success">In Stock</span>
              ) : product.availableStock > 0 ? (
                <span className="text-success">{product.availableStock} in stock</span>
              ) : (
                <span className="text-error">Out of Stock</span>
              )}
            </div>

            {/* Add to Cart */}
            <div className="flex gap-4 mb-8">
              <button
                onClick={handleAddToCart}
                disabled={product.availableStock === 0}
                className="btn-primary flex-1 py-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {product.availableStock === 0 ? 'Out of Stock' : 'Add to Cart'}
              </button>
            </div>

            {/* Tags */}
            {product.tags && product.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {product.tags.map((tag: any) => (
                  <Link
                    key={tag.id}
                    to={`/products?tag=${tag.slug}`}
                    className="px-3 py-1 text-sm bg-leather text-text-secondary rounded-full hover:bg-gold/10 hover:text-gold transition-colors"
                  >
                    {tag.name}
                  </Link>
                ))}
              </div>
            )}

            {/* Info */}
            <div className="border-t border-gold/10 pt-6">
              <div className="flex items-center gap-3 text-sm text-text-secondary mb-3">
                <span>&#128274;</span>
                <span>Secure payment with card or crypto</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-text-secondary mb-3">
                <span>&#9889;</span>
                <span>Instant delivery after purchase</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-text-secondary">
                <span>&#128176;</span>
                <span>Satisfaction guaranteed</span>
              </div>
            </div>
          </div>
        </div>

        {/* Full Description */}
        <div className="mt-12">
          <h2 className="font-western text-2xl text-cream mb-6">Description</h2>
          <div className="card p-6">
            <div className="prose prose-invert max-w-none">
              {product.description.split('\n').map((paragraph: string, i: number) => (
                <p key={i} className="text-text-secondary mb-4">{paragraph}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
